import * as Y from 'yjs';
import { v4 as uuidv4 } from 'uuid';
import {
    NodeInstance,
    DataConnection,
    GlueVariable,
    FunctionDefinition,
    FunctionPortDefinition,
    Vector,
    IntegrationAccess,
    UserDefinedType,
    DataValue,
    NodeInstanceState,
    isFunctionReferenceValue,
} from '@glueapp/graph-types';
import {
    NODE_LEGACY_INT_ID,
    NODE_POSITION,
    NODE_POSITION_X,
    NODE_POSITION_Y,
    NODE_WIDTH,
    NODE_HEIGHT,
    NODE_SELECTED_INTEGRATION_ACCESS,
    NODE_SELECTED_VARIABLE,
    NODE_PORT_VALUES,
    NODE_PORT_USER_DEFINED_TYPES,
    NODE_STATE,
    CONNECTION_ID,
    CONNECTION_LEGACY_INT_ID,
    CONNECTION_SENDING_NODE_ID,
    CONNECTION_SENDING_PORT_NAME,
    CONNECTION_SENDING_PORT_TYPE,
    CONNECTION_RECEIVING_NODE_ID,
    CONNECTION_RECEIVING_PORT_NAME,
    CONNECTION_RECEIVING_PORT_TYPE,
    META_KEY,
    META_NEXT_LEGACY_INT_ID,
    NODES_KEY,
    CONNECTIONS_KEY,
    VARIABLES_KEY,
    FUNCTION_NAME,
    FUNCTION_NODES,
    FUNCTION_CONNECTIONS,
    FUNCTION_INPUT_PORT_DEFINITIONS,
    FUNCTION_OUTPUT_PORT_DEFINITIONS,
    FUNCTION_UPDATED_AT,
    FUNCTION_VERSION,
    getFunctionsMap,
} from './yDocSchema';
import { encodeDataValue, decodeDataValueMap, replaceEncodedDataValueMap } from './dataValueCodec';
import { encodeNode, encodeConnection, encodeVariable, encodeFunction, encodeFunctionPortDefinition } from './encoders';

/**
 * Y.Doc mutators — one exported function per reducer action.
 *
 * Every mutator wraps its work in `yDoc.transact(fn, options.origin ?? yDoc.clientID)`
 * so that:
 *   - The local UndoManager (which tracks the user's clientID) picks up local edits.
 *   - Remote peers' transactions stay out of the local undo stack.
 *   - Agent / system / hydration writes can override origin to opt out of undo.
 *
 * Design constraints:
 *   - Mutators are a thin write-layer. They do NOT compute layout, look up node
 *     templates, or translate IDs. Callers (the dispatcher in the frontend) prepare
 *     fully-formed `NodeInstance` / `DataConnection` / `FunctionDefinition` data
 *     using the latest snapshot, then hand it off here.
 *   - UUID minting happens inside the transaction (not snapshot-derivable).
 *   - `legacyIntId` is supplied by the caller (snapshot-derived). Each mutator
 *     bumps `meta.nextLegacyIntId` to `Math.max(current, supplied + 1)` so future
 *     replicas see a high-water-mark even when offline. Concurrent allocations
 *     across replicas can produce duplicate `legacyIntId`s briefly — known v1
 *     limitation, surfaces as ordering jitter in `yDocToJson`. Phase 4's
 *     reconciler renumbers if needed.
 *   - Cascade behavior (e.g. `removeNodes` deletes referencing connections) is
 *     resolved inside the transaction by reading the live doc state.
 *   - Selection state lives in awareness, not the doc — selection actions do not
 *     produce mutators here.
 */

export interface MutatorOptions {
    /** Override the Y.Doc transaction origin. Defaults to `yDoc.clientID`. */
    origin?: unknown;
    /** When set, the mutator targets the named function's nested nodes/connections. */
    parentFunctionId?: string;
}

function transact(yDoc: Y.Doc, options: MutatorOptions | undefined, fn: () => void): void {
    yDoc.transact(fn, options?.origin ?? yDoc.clientID);
}

function getScopedNodesMap(yDoc: Y.Doc, parentFunctionId: string | undefined): Y.Map<Y.Map<unknown>> {
    if (!parentFunctionId) return yDoc.getMap<Y.Map<unknown>>(NODES_KEY);
    const yFn = getFunctionsMap(yDoc).get(parentFunctionId);
    if (!yFn) {
        throw new Error(`yDocMutator: function "${parentFunctionId}" not found`);
    }
    return yFn.get(FUNCTION_NODES) as Y.Map<Y.Map<unknown>>;
}

function getScopedConnectionsMap(yDoc: Y.Doc, parentFunctionId: string | undefined): Y.Map<Y.Map<unknown>> {
    if (!parentFunctionId) return yDoc.getMap<Y.Map<unknown>>(CONNECTIONS_KEY);
    const yFn = getFunctionsMap(yDoc).get(parentFunctionId);
    if (!yFn) {
        throw new Error(`yDocMutator: function "${parentFunctionId}" not found`);
    }
    return yFn.get(FUNCTION_CONNECTIONS) as Y.Map<Y.Map<unknown>>;
}

function findNodeByLegacyId(nodesMap: Y.Map<Y.Map<unknown>>, legacyId: number): Y.Map<unknown> | undefined {
    for (const yNode of nodesMap.values()) {
        if ((yNode.get(NODE_LEGACY_INT_ID) as number) === legacyId) return yNode;
    }
    return undefined;
}

function bumpLegacyIntIdWatermark(yDoc: Y.Doc, allocated: number): void {
    const meta = yDoc.getMap(META_KEY);
    const current = (meta.get(META_NEXT_LEGACY_INT_ID) as number | undefined) ?? 0;
    if (allocated >= current) {
        meta.set(META_NEXT_LEGACY_INT_ID, allocated + 1);
    }
}

function nodeUuidByLegacyId(nodesMap: Y.Map<Y.Map<unknown>>, legacyId: number): string | undefined {
    for (const [uuid, yNode] of nodesMap.entries()) {
        if ((yNode.get(NODE_LEGACY_INT_ID) as number) === legacyId) return uuid;
    }
    return undefined;
}

// ---------------------------------------------------------------------------
// Nodes
// ---------------------------------------------------------------------------

/**
 * Add a fully-formed node to the graph. Caller is responsible for picking
 * `node.id` from the snapshot's max + 1 (matching the legacy reducer).
 */
export function addNode(yDoc: Y.Doc, node: NodeInstance, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const uuid = uuidv4();
        nodesMap.set(uuid, encodeNode(node, uuid));
        bumpLegacyIntIdWatermark(yDoc, node.id);
    });
}

/**
 * Remove nodes by legacy int id. Cascades to delete every connection that
 * references any of the removed nodes (in the same scope).
 */
export function removeNodes(yDoc: Y.Doc, legacyIds: number[], options?: MutatorOptions): void {
    if (legacyIds.length === 0) return;
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const connectionsMap = getScopedConnectionsMap(yDoc, options?.parentFunctionId);
        const targetIds = new Set(legacyIds);

        const removedUuids: string[] = [];
        for (const [uuid, yNode] of nodesMap.entries()) {
            if (targetIds.has(yNode.get(NODE_LEGACY_INT_ID) as number)) {
                removedUuids.push(uuid);
            }
        }
        for (const uuid of removedUuids) {
            nodesMap.delete(uuid);
        }

        const removedUuidSet = new Set(removedUuids);
        const orphanedConnUuids: string[] = [];
        for (const [uuid, yConn] of connectionsMap.entries()) {
            const sending = yConn.get(CONNECTION_SENDING_NODE_ID) as string;
            const receiving = yConn.get(CONNECTION_RECEIVING_NODE_ID) as string;
            if (removedUuidSet.has(sending) || removedUuidSet.has(receiving)) {
                orphanedConnUuids.push(uuid);
            }
        }
        for (const uuid of orphanedConnUuids) {
            connectionsMap.delete(uuid);
        }
    });
}

/**
 * Translate the position of every node whose legacy id is in `legacyIds` by
 * `delta`. The caller passes the explicit ID set (typically the local user's
 * awareness `selectedLegacyIds`) so the mutator never has to read live
 * selection state.
 */
export function moveNodes(yDoc: Y.Doc, legacyIds: number[], delta: Vector, options?: MutatorOptions): void {
    if (legacyIds.length === 0 || (delta.x === 0 && delta.y === 0)) return;
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const targetIds = new Set(legacyIds);
        for (const yNode of nodesMap.values()) {
            const legacyId = yNode.get(NODE_LEGACY_INT_ID) as number | undefined;
            if (legacyId === undefined || !targetIds.has(legacyId)) continue;
            const position = yNode.get(NODE_POSITION) as Y.Map<number> | undefined;
            if (!position) continue;
            position.set(NODE_POSITION_X, (position.get(NODE_POSITION_X) ?? 0) + delta.x);
            position.set(NODE_POSITION_Y, (position.get(NODE_POSITION_Y) ?? 0) + delta.y);
        }
    });
}

export function setNodeDimensions(yDoc: Y.Doc, legacyId: number, dimensions: Vector, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, legacyId);
        if (!yNode) return;
        yNode.set(NODE_WIDTH, dimensions.x);
        yNode.set(NODE_HEIGHT, dimensions.y);
    });
}

/**
 * Wholesale-replace the entire `state` map for a node: every existing key is
 * deleted and the provided entries are re-set. Concurrent peer writes to the
 * same node's state map may be lost because the delete-all-then-set pattern
 * destroys keys this caller does not know about.
 */
export function setNodeState(yDoc: Y.Doc, legacyId: number, state: NodeInstanceState, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, legacyId);
        if (!yNode) return;
        const stateMap = yNode.get(NODE_STATE) as Y.Map<Y.Map<unknown>>;
        replaceEncodedDataValueMap(stateMap, state);
    });
}

export function setNodeSelectedIntegrationAccess(
    yDoc: Y.Doc,
    legacyId: number,
    integrationAccess: IntegrationAccess | undefined,
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, legacyId);
        if (!yNode) return;
        if (integrationAccess === undefined) {
            yNode.delete(NODE_SELECTED_INTEGRATION_ACCESS);
        } else {
            yNode.set(NODE_SELECTED_INTEGRATION_ACCESS, JSON.stringify(integrationAccess));
        }
    });
}

export function setNodeSelectedVariable(yDoc: Y.Doc, legacyId: number, variableName: string | undefined, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, legacyId);
        if (!yNode) return;
        if (variableName === undefined) {
            yNode.delete(NODE_SELECTED_VARIABLE);
        } else {
            yNode.set(NODE_SELECTED_VARIABLE, variableName);
        }
    });
}

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

/**
 * Add a connection. Auto-replaces any existing connection on the same
 * receiving (node, port) endpoint — concurrent collisions converge to a
 * single connection per port via deterministic dedupe in `yDocToJson`
 * combined with Phase 4's reconciler.
 *
 * Caller supplies `legacyIntId` derived from the snapshot's max + 1; this
 * function bumps `meta.nextLegacyIntId` accordingly.
 */
export function addConnection(yDoc: Y.Doc, connection: Omit<DataConnection, 'id'>, legacyIntId: number, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const connectionsMap = getScopedConnectionsMap(yDoc, options?.parentFunctionId);

        const sendingUuid = nodeUuidByLegacyId(nodesMap, connection.sendingNodeInstanceId);
        const receivingUuid = nodeUuidByLegacyId(nodesMap, connection.receivingNodeInstanceId);
        if (sendingUuid === undefined || receivingUuid === undefined) {
            // Dangling connection — caller's snapshot is stale. Skip.
            return;
        }

        // Auto-replace: drop any existing connection terminating at the same receiving port.
        const replacedUuids: string[] = [];
        for (const [uuid, yConn] of connectionsMap.entries()) {
            if (
                (yConn.get(CONNECTION_RECEIVING_NODE_ID) as string) === receivingUuid &&
                (yConn.get(CONNECTION_RECEIVING_PORT_NAME) as string) === connection.receivingPortName
            ) {
                replacedUuids.push(uuid);
            }
        }
        for (const uuid of replacedUuids) {
            connectionsMap.delete(uuid);
        }

        const uuid = uuidv4();
        connectionsMap.set(uuid, encodeConnection({ ...connection, id: legacyIntId } as DataConnection, uuid, sendingUuid, receivingUuid));
        bumpLegacyIntIdWatermark(yDoc, legacyIntId);
    });
}

export function removeConnection(yDoc: Y.Doc, legacyIntId: number, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const connectionsMap = getScopedConnectionsMap(yDoc, options?.parentFunctionId);
        for (const [uuid, yConn] of connectionsMap.entries()) {
            if ((yConn.get(CONNECTION_LEGACY_INT_ID) as number) === legacyIntId) {
                connectionsMap.delete(uuid);
                return;
            }
        }
    });
}

export function removeConnectionByEndpoints(
    yDoc: Y.Doc,
    sendingNodeInstanceId: number,
    sendingPortName: string,
    receivingNodeInstanceId: number,
    receivingPortName: string,
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const connectionsMap = getScopedConnectionsMap(yDoc, options?.parentFunctionId);
        const sendingUuid = nodeUuidByLegacyId(nodesMap, sendingNodeInstanceId);
        const receivingUuid = nodeUuidByLegacyId(nodesMap, receivingNodeInstanceId);
        if (sendingUuid === undefined || receivingUuid === undefined) return;

        const matchingUuids: string[] = [];
        for (const [uuid, yConn] of connectionsMap.entries()) {
            if (
                (yConn.get(CONNECTION_SENDING_NODE_ID) as string) === sendingUuid &&
                (yConn.get(CONNECTION_SENDING_PORT_NAME) as string) === sendingPortName &&
                (yConn.get(CONNECTION_RECEIVING_NODE_ID) as string) === receivingUuid &&
                (yConn.get(CONNECTION_RECEIVING_PORT_NAME) as string) === receivingPortName
            ) {
                matchingUuids.push(uuid);
            }
        }
        for (const uuid of matchingUuids) {
            connectionsMap.delete(uuid);
        }
    });
}

// ---------------------------------------------------------------------------
// Ports
// ---------------------------------------------------------------------------

export function setPortValueAndType(
    yDoc: Y.Doc,
    legacyNodeId: number,
    portName: string,
    value: DataValue | undefined,
    userDefinedType?: UserDefinedType,
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, legacyNodeId);
        if (!yNode) return;

        const portValuesMap = yNode.get(NODE_PORT_VALUES) as Y.Map<Y.Map<unknown>>;
        if (value === undefined) {
            portValuesMap.delete(portName);
        } else {
            portValuesMap.set(portName, encodeDataValue(value));
        }

        if (userDefinedType !== undefined) {
            const typesMap = yNode.get(NODE_PORT_USER_DEFINED_TYPES) as Y.Map<unknown>;
            typesMap.set(portName, JSON.stringify(userDefinedType));
        }
    });
}

export function setPortUserDefinedType(
    yDoc: Y.Doc,
    legacyNodeId: number,
    portName: string,
    userDefinedType: UserDefinedType | undefined,
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, legacyNodeId);
        if (!yNode) return;
        const typesMap = yNode.get(NODE_PORT_USER_DEFINED_TYPES) as Y.Map<unknown>;
        if (userDefinedType === undefined) {
            typesMap.delete(portName);
        } else {
            typesMap.set(portName, JSON.stringify(userDefinedType));
        }
    });
}

export function setFunctionPortValue(
    yDoc: Y.Doc,
    legacyNodeId: number,
    portName: string,
    referencedFunctionId: string | undefined,
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, legacyNodeId);
        if (!yNode) return;
        const portValuesMap = yNode.get(NODE_PORT_VALUES) as Y.Map<Y.Map<unknown>>;
        if (referencedFunctionId === undefined) {
            portValuesMap.delete(portName);
        } else {
            portValuesMap.set(portName, encodeDataValue({ isFunctionReferenceValue: true, functionId: referencedFunctionId }));
        }
    });
}

// ---------------------------------------------------------------------------
// Variables
// ---------------------------------------------------------------------------

export function addVariable(yDoc: Y.Doc, variable: GlueVariable, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const variablesMap = yDoc.getMap<Y.Map<unknown>>(VARIABLES_KEY);
        if (variablesMap.has(variable.name)) return;
        variablesMap.set(variable.name, encodeVariable(variable));
    });
}

/**
 * Rename or otherwise update a variable. If `oldName !== variable.name`, every
 * `selectedVariable` reference (top-level and inside every function) is renamed
 * in the same transaction.
 */
export function updateVariable(yDoc: Y.Doc, oldName: string, variable: GlueVariable, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const variablesMap = yDoc.getMap<Y.Map<unknown>>(VARIABLES_KEY);
        if (!variablesMap.has(oldName)) return;

        if (oldName !== variable.name) {
            variablesMap.delete(oldName);
            variablesMap.set(variable.name, encodeVariable(variable));
            renameSelectedVariableEverywhere(yDoc, oldName, variable.name);
        } else {
            variablesMap.set(variable.name, encodeVariable(variable));
        }
    });
}

function renameSelectedVariableEverywhere(yDoc: Y.Doc, oldName: string, newName: string): void {
    const renameInScope = (nodesMap: Y.Map<Y.Map<unknown>>) => {
        for (const yNode of nodesMap.values()) {
            if ((yNode.get(NODE_SELECTED_VARIABLE) as string | undefined) === oldName) {
                yNode.set(NODE_SELECTED_VARIABLE, newName);
            }
        }
    };
    renameInScope(yDoc.getMap<Y.Map<unknown>>(NODES_KEY));
    for (const yFn of getFunctionsMap(yDoc).values()) {
        const fnNodes = yFn.get(FUNCTION_NODES) as Y.Map<Y.Map<unknown>>;
        renameInScope(fnNodes);
    }
}

export function deleteVariable(yDoc: Y.Doc, name: string, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const variablesMap = yDoc.getMap<Y.Map<unknown>>(VARIABLES_KEY);
        variablesMap.delete(name);
    });
}

/**
 * Replace the entire variables collection. Used by the legacy `SET_VARIABLES`
 * action, typically for bulk updates from server-side reconciliation.
 */
export function setVariables(yDoc: Y.Doc, variables: GlueVariable[], options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const variablesMap = yDoc.getMap<Y.Map<unknown>>(VARIABLES_KEY);
        const incomingNames = new Set(variables.map(variable => variable.name));
        for (const existingName of Array.from(variablesMap.keys())) {
            if (!incomingNames.has(existingName)) variablesMap.delete(existingName);
        }
        for (const variable of variables) {
            variablesMap.set(variable.name, encodeVariable(variable));
        }
    });
}

// ---------------------------------------------------------------------------
// Functions
// ---------------------------------------------------------------------------

export function addFunction(yDoc: Y.Doc, fn: FunctionDefinition, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        if (functionsMap.has(fn.id)) return;
        functionsMap.set(fn.id, encodeFunction(fn, uuidv4));
        // Bump watermark over any inner-node legacy ids carried by this function.
        let max = 0;
        for (const node of fn.nodeInstances) if (node.id > max) max = node.id;
        for (const conn of fn.connections) if (conn.id > max) max = conn.id;
        if (max > 0) bumpLegacyIntIdWatermark(yDoc, max);
    });
}

/**
 * Replace the function definition wholesale. Caller bumps `version` and
 * `updatedAt` to match reducer behavior.
 */
export function updateFunction(yDoc: Y.Doc, functionId: string, updatedFunction: FunctionDefinition, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        if (!functionsMap.has(functionId)) return;
        functionsMap.set(functionId, encodeFunction(updatedFunction, uuidv4));
    });
}

export function updateFunctionName(yDoc: Y.Doc, functionId: string, name: string, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        const yFn = functionsMap.get(functionId);
        if (!yFn) return;
        const previousVersion = (yFn.get(FUNCTION_VERSION) as number) ?? 0;
        yFn.set(FUNCTION_NAME, name);
        yFn.set(FUNCTION_VERSION, previousVersion + 1);
        yFn.set(FUNCTION_UPDATED_AT, new Date().toISOString());

        // Mirror reducer behavior: every function-instance node referencing this
        // function id gets its `state.functionName` updated so the visible label
        // tracks the rename.
        const nameValue = encodeDataValue(name);
        const updateNodeStateInScope = (nodesMap: Y.Map<Y.Map<unknown>>) => {
            for (const yNode of nodesMap.values()) {
                const functionIdValue = yNode.get('functionId') as string | undefined;
                if (functionIdValue !== functionId) continue;
                const stateMap = yNode.get(NODE_STATE) as Y.Map<Y.Map<unknown>>;
                stateMap.set('functionName', nameValue);
            }
        };
        updateNodeStateInScope(yDoc.getMap<Y.Map<unknown>>(NODES_KEY));
        for (const yOtherFn of functionsMap.values()) {
            const fnNodes = yOtherFn.get(FUNCTION_NODES) as Y.Map<Y.Map<unknown>>;
            updateNodeStateInScope(fnNodes);
        }
    });
}

/**
 * Delete a function. Cleans up `FunctionReferenceValue` port values across the
 * top-level graph and every other function in the same transaction.
 */
export function deleteFunction(yDoc: Y.Doc, functionId: string, options?: MutatorOptions): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        if (!functionsMap.has(functionId)) return;
        functionsMap.delete(functionId);

        const cleanScope = (nodesMap: Y.Map<Y.Map<unknown>>) => {
            for (const yNode of nodesMap.values()) {
                const portValuesMap = yNode.get(NODE_PORT_VALUES) as Y.Map<Y.Map<unknown>>;
                const decoded = decodeDataValueMap(portValuesMap);
                for (const [portName, value] of Object.entries(decoded)) {
                    if (isFunctionReferenceValue(value) && value.functionId === functionId) {
                        portValuesMap.delete(portName);
                    }
                }
            }
        };
        cleanScope(yDoc.getMap<Y.Map<unknown>>(NODES_KEY));
        for (const yOtherFn of functionsMap.values()) {
            const fnNodes = yOtherFn.get(FUNCTION_NODES) as Y.Map<Y.Map<unknown>>;
            cleanScope(fnNodes);
        }
    });
}

export function updateFunctionInputs(
    yDoc: Y.Doc,
    functionId: string,
    inputPortDefinitions: FunctionPortDefinition[],
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        const yFn = functionsMap.get(functionId);
        if (!yFn) return;

        // Reject duplicate names — match reducer behavior.
        const seen = new Set<string>();
        for (const port of inputPortDefinitions) {
            if (seen.has(port.name)) return;
            seen.add(port.name);
        }

        const previousVersion = (yFn.get(FUNCTION_VERSION) as number) ?? 0;
        const inputs = new Y.Array<Y.Map<unknown>>();
        inputs.push(inputPortDefinitions.map(encodeFunctionPortDefinition));
        yFn.set(FUNCTION_INPUT_PORT_DEFINITIONS, inputs);
        yFn.set(FUNCTION_VERSION, previousVersion + 1);
        yFn.set(FUNCTION_UPDATED_AT, new Date().toISOString());
    });
}

export function updateFunctionOutputs(
    yDoc: Y.Doc,
    functionId: string,
    outputPortDefinitions: FunctionPortDefinition[],
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        const yFn = functionsMap.get(functionId);
        if (!yFn) return;

        const seen = new Set<string>();
        for (const port of outputPortDefinitions) {
            if (seen.has(port.name)) return;
            seen.add(port.name);
        }

        const previousVersion = (yFn.get(FUNCTION_VERSION) as number) ?? 0;
        const outputs = new Y.Array<Y.Map<unknown>>();
        outputs.push(outputPortDefinitions.map(encodeFunctionPortDefinition));
        yFn.set(FUNCTION_OUTPUT_PORT_DEFINITIONS, outputs);
        yFn.set(FUNCTION_VERSION, previousVersion + 1);
        yFn.set(FUNCTION_UPDATED_AT, new Date().toISOString());
    });
}

/**
 * Add a function-instance node referencing an existing function. The caller
 * (frontend dispatcher) prepares the `FunctionNodeInstance` from the function's
 * port definitions via `createFunctionNodeInstance`.
 */
export function addFunctionInstance(yDoc: Y.Doc, functionNode: NodeInstance, options?: MutatorOptions): void {
    addNode(yDoc, functionNode, options);
}

/**
 * Atomic creation of a brand-new function plus an instance node placed in the
 * current scope. Caller pre-builds both the `FunctionDefinition` and the
 * matching `FunctionNodeInstance` (with id chosen from snapshot max + 1).
 */
export function createFunctionWithInstance(
    yDoc: Y.Doc,
    fn: FunctionDefinition,
    functionNode: NodeInstance,
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        if (!functionsMap.has(fn.id)) {
            functionsMap.set(fn.id, encodeFunction(fn, uuidv4));
        }
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const uuid = uuidv4();
        nodesMap.set(uuid, encodeNode(functionNode, uuid));
        bumpLegacyIntIdWatermark(yDoc, functionNode.id);
    });
}

/**
 * Atomic creation of a function from a port signature plus a function-reference
 * value injected on the originating port. Used by the "Promote to function"
 * affordance. Mirrors the reducer's `CREATE_FUNCTION_FROM_SIGNATURE` shape.
 */
export function createFunctionFromSignature(
    yDoc: Y.Doc,
    fn: FunctionDefinition,
    nodeInstanceId: number,
    portName: string,
    options?: MutatorOptions,
): void {
    transact(yDoc, options, () => {
        const functionsMap = getFunctionsMap(yDoc);
        if (!functionsMap.has(fn.id)) {
            functionsMap.set(fn.id, encodeFunction(fn, uuidv4));
        }
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const yNode = findNodeByLegacyId(nodesMap, nodeInstanceId);
        if (!yNode) return;
        const portValuesMap = yNode.get(NODE_PORT_VALUES) as Y.Map<Y.Map<unknown>>;
        portValuesMap.set(portName, encodeDataValue({ isFunctionReferenceValue: true, functionId: fn.id }));
    });
}

// ---------------------------------------------------------------------------
// Bulk
// ---------------------------------------------------------------------------

/**
 * Paste / duplicate. Caller has pre-translated `nodes`/`connections` to fresh
 * ids (snapshot.max + 1, +2, ...). Mutator just writes them.
 *
 * `deselectOld` is a no-op here — selection lives in awareness, so the caller
 * clears `selectedLegacyIds` separately.
 */
export function mergeInGraph(yDoc: Y.Doc, nodes: NodeInstance[], connections: DataConnection[], options?: MutatorOptions): void {
    if (nodes.length === 0 && connections.length === 0) return;
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const connectionsMap = getScopedConnectionsMap(yDoc, options?.parentFunctionId);

        const legacyIdToUuid = new Map<number, string>();
        for (const [uuid, yNode] of nodesMap.entries()) {
            legacyIdToUuid.set(yNode.get(NODE_LEGACY_INT_ID) as number, uuid);
        }

        let watermark = 0;
        for (const node of nodes) {
            const uuid = uuidv4();
            nodesMap.set(uuid, encodeNode(node, uuid));
            legacyIdToUuid.set(node.id, uuid);
            if (node.id > watermark) watermark = node.id;
        }
        for (const connection of connections) {
            const sendingUuid = legacyIdToUuid.get(connection.sendingNodeInstanceId);
            const receivingUuid = legacyIdToUuid.get(connection.receivingNodeInstanceId);
            if (sendingUuid === undefined || receivingUuid === undefined) continue;
            const uuid = uuidv4();
            connectionsMap.set(uuid, encodeConnection(connection, uuid, sendingUuid, receivingUuid));
            if (connection.id > watermark) watermark = connection.id;
        }
        if (watermark > 0) bumpLegacyIntIdWatermark(yDoc, watermark);
    });
}

/**
 * Apply pre-computed position updates from the layout engine. Caller invokes
 * `applyPositionUpdatesWithCenterPreservation` (or whatever layout helper)
 * outside the transaction and hands us the final `{ legacyId, x, y }` tuples.
 */
export function layoutGraph(yDoc: Y.Doc, positionUpdates: { legacyId: number; x: number; y: number }[], options?: MutatorOptions): void {
    if (positionUpdates.length === 0) return;
    transact(yDoc, options, () => {
        const nodesMap = getScopedNodesMap(yDoc, options?.parentFunctionId);
        const updatesByLegacyId = new Map(positionUpdates.map(update => [update.legacyId, update]));
        for (const yNode of nodesMap.values()) {
            const legacyId = yNode.get(NODE_LEGACY_INT_ID) as number;
            const update = updatesByLegacyId.get(legacyId);
            if (!update) continue;
            const position = yNode.get(NODE_POSITION) as Y.Map<number>;
            position.set(NODE_POSITION_X, update.x);
            position.set(NODE_POSITION_Y, update.y);
        }
    });
}
